import React, { useState } from 'react';
import { Paper, Stack, Box, Typography, Button, TextField, Snackbar, Alert } from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import supportService from '../services/SupportService';
import { usePostHog } from '@posthog/react';

const schema = yup.object({
  title: yup.string().required('Title is required'),
  description: yup.string().required('Description is required').min(10, 'Write more detail'),
});

const SupportCard = ({ linkedProfileId, linkedProfileType, linkedProfileName, onCreated }) => {
  const { control, handleSubmit, reset } = useForm({ resolver: yupResolver(schema) });
  const posthog = usePostHog();
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      const payload = {
        title: data.title,
        description: data.description,
        linkedProfileId: linkedProfileId || undefined,
        linkedProfileType: linkedProfileType || undefined,
        linkedProfileName: linkedProfileName || undefined,
      };
      const created = await supportService.createCase(payload);
      posthog?.capture('support_ticket_submitted', { ticket_id: created?.id, profile_type: linkedProfileType });
      setSnackbar({ open: true, message: 'Your ticket has been submitted. Our team will review it shortly.', severity: 'success' });
      reset();
      if (typeof onCreated === 'function') onCreated(created)
    } catch (err) {
      console.error('Failed to create support case', err);
      setSnackbar({ open: true, message: err.message || 'Failed to create ticket', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper elevation={0} sx={{ p: 4, borderRadius: 3, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
      <Stack spacing={3}>
        <Box>
          <Typography variant="h6" fontWeight={800} color="primary.main">Open a New Ticket</Typography>
          <Typography variant="body2" color="text.secondary">Please provide as much detail as possible so we can help you faster.</Typography>
        </Box>

        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack spacing={2.5}>
            <Controller
              name="title"
              control={control}
              defaultValue=""
              render={({ field, fieldState }) => (
                <TextField 
                  label="Subject / Topic" 
                  placeholder="e.g. Issue with my application"
                  fullWidth 
                  size="small" 
                  {...field} 
                  error={!!fieldState.error} 
                  helperText={fieldState.error?.message}
                  sx={{ bgcolor: 'background.paper' }}
                />
              )}
            />

            <Controller
              name="description"
              control={control}
              defaultValue=""
              render={({ field, fieldState }) => (
                <TextField 
                  label="Detailed Description" 
                  placeholder="Tell us exactly what happened..."
                  fullWidth 
                  multiline 
                  minRows={5} 
                  size="small" 
                  {...field} 
                  error={!!fieldState.error} 
                  helperText={fieldState.error?.message} 
                  sx={{ bgcolor: 'background.paper' }}
                />
              )}
            />

            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button type="submit" variant="contained" size="large" disabled={loading} sx={{ px: 4, borderRadius: 2 }}>
                {loading ? 'Submitting...' : 'Submit Ticket'}
              </Button>
            </Box>
          </Stack>
        </form>
      </Stack>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>{snackbar.message}</Alert>
      </Snackbar>
    </Paper>
  );
};

export default SupportCard;
